import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChildren,
} from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  ReplaySubject,
  Subscription,
} from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import {
  CustomFormData,
  FormFields,
  OrderFormValidationResult,
} from '@orderform/widgets';
import {
  AbstractControl,
  FormControl,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { OrderformFascade } from '../../fascades/orderform.fascade';
import { formMarkAllAsTouched } from '@digistore/ds24-ui/rxjs';
import { InterfaceField, InterfaceFields } from './inputs-generator.model';
import {
  isFormFieldVisible,
  mapFieldListToFormStructure,
} from './map-field-list-to-form-structure';
import { GeneratorInputComponent } from './generator-input/generator-input.component';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { isPageBuilderElementVisible } from '../../misc/pagebuilder';
import { getInvalidFieldListFromFormGroup } from '../../misc/get-invalid-field-list-from-form-group';
import * as isEqual from 'lodash/isEqual';

// function decodeHtml(html) {
//   const txt = document.createElement('textarea');
//   txt.innerHTML = html;
//   return txt.value;
// }

export const INFO_TEXT_WIDGET_DISPLAY = $localize`:@@global.info.widget_display:Diese Komponente wird nur angezeigt,
wenn die erforderlichen Optionen in Ihren Produkteinstellungen aktiviert sind.`;

export const INFO_TEXT_WIDGET_DISPLAY_ON_ADDITIONAL_FIELDS = $localize`:@@global.info.widget_display_on_additional_fields:Diese Komponente wird nur angezeigt,
  wenn für das ausgewählte Produkt zusätzliche Eingabefelder hinterlegt sind.`;

@Component({
  selector: 'orderform-inputs-generator',
  templateUrl: './inputs-generator.component.html',
  styleUrls: ['./inputs-generator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputsGeneratorComponent implements OnInit, OnDestroy {
  @ViewChildren(GeneratorInputComponent)
  generatorInputs: GeneratorInputComponent[];
  fields$ = new BehaviorSubject<FormFields.Field[]>([]);

  data$ = new BehaviorSubject<CustomFormData>({});

  private _saveFormGroupValueChange: Subscription;
  private _formGroup = new UntypedFormGroup({});
  private _destroy$ = new ReplaySubject<void>(1);
  private _isAutomaticValueChangeActive = false;

  @Input() set fields(fields: FormFields.Field[]) {
    const modFields: FormFields.Field[] = [];

    fields.forEach((field) => {
      /**
       * Wir haben im BE unter den Custom Forms ein Dropdown welches mehrere Auswahlmöglichkeiten haben kann.
       * Diese Auswahl des Drodowns sollen als Checkbox angezeigt werden. Aufgrund, dass wir aus dem BE nur ein Element erhalten
       * aber an das BE mehrere Daten seperat schicken müssen, generieren wir der einfacherhalb einige Hidden Elements die den Checkboxen zugeordnet werden
       * Grund: KISS
       */
      if (this._fieldIsCheckboxList(field)) {
        (field.options as FormFields.SelectOption[]).forEach((option) => {
          const [key] = Object.keys(option);
          const [value] = Object.values(option);
          const optionPostname = field.postname + '_' + key;
          modFields.push({
            type: 'hidden',
            postname: optionPostname,
            id: field.id + '_' + key,
            label: value,
          });
        });
      }
      modFields.push(field);
    });

    this.fields$.next(modFields);
  }

  @Input() set data(data: CustomFormData) {
    this.data$.next(data || {});
  }

  @Output() readonly dataChanges = new EventEmitter<CustomFormData>();
  @Output() readonly stateChange = this._formGroup.statusChanges.pipe(
    distinctUntilChanged()
  );
  private _excludeFormOrderValidation = false;
  @Input() set excludeFromOrderValidation(exclude: any) {
    this._excludeFormOrderValidation = coerceBooleanProperty(exclude);
  }

  /**
   * Attention!
   * This form will not regenerate of this.data$ will changed! see _syncData()
   */
  formGroup$ = combineLatest([
    this.fields$.pipe(distinctUntilChanged()),
    this._orderFormFascade.requestPending$.pipe(
      // emit value only if data had comes from BE
      map((onUpdated) => {
        return this.data$.value;
      }),
      distinctUntilChanged()
    ),
    this._orderFormFascade.validation$.pipe(
      map((data) => data.errors_by_postname),
      distinctUntilChanged()
    ),
  ]).pipe(
    tap(() => {
      this._isAutomaticValueChangeActive = true;
      if (this._saveFormGroupValueChange) {
        this._saveFormGroupValueChange.unsubscribe();
      }
    }),
    map(([fields, preFilledCustomForm, backendValidationError]) => {
      const formGroup = this._formGroup;
      // find controls to remove
      Object.keys(formGroup.controls)
        .filter((controlName) => {
          return !fields.find((field) => field.postname === controlName);
        })
        .forEach((controlName) => {
          if (formGroup.contains(controlName)) {
            formGroup.removeControl(controlName);
          }
        });

      /**
       * Foreach each field of custom form
       */
      fields
        /**
         * Filter only input for formControls
         */
        .filter((field) => ['headline'].indexOf(field.type) === -1)
        .forEach((field) => {
          const lastField = formGroup.get(field.postname);
          const lastFieldStatus = {
            dirty: lastField ? lastField.dirty : false,
            touched: lastField ? lastField.touched : false,
            invalid: lastField ? lastField.invalid : false,
          };

          const inputValue = this._getInputValue(field, preFilledCustomForm);

          /**
           * Create / Update Control and set value
           */
          if (!formGroup.get(field.postname)) {
            formGroup.setControl(
              field.postname,
              new UntypedFormControl({
                value: inputValue,
                /**
                 * Do not disable control because disabled controls do not emit values
                 */
                disabled: false,
              })
            );
          } else if (!(formGroup.get(field.postname) as FormControl).touched) {
            (formGroup.get(field.postname) as FormControl).setValue(
              inputValue,
              { emitEvent: false }
            );
          }
          const control = formGroup.get(field.postname);

          /**
           * Set validations
           */
          const validation: ValidatorFn[] = this._getFieldValidatorList(
            control,
            field,
            backendValidationError
          );
          control.setValidators(validation);

          if (field.asyncValidators) {
            control.setAsyncValidators(field.asyncValidators);
          }

          /**
           * Status vom letzten FormControl übertragen.
           */
          // check last stats
          if (
            lastFieldStatus.touched ||
            !!backendValidationError[field.postname]
          ) {
            control.markAsTouched();
          }

          if (backendValidationError[field.postname]) {
            control.markAsPristine();
          }

          if (lastFieldStatus.invalid) {
            control.updateValueAndValidity();
          }
        });

      // set all inputs to touched =>  Displays errors instantly
      if (this._orderFormFascade.submitted$.value) {
        formGroup.markAllAsTouched();
      }

      return formGroup;
    }),
    tap((formGroup) => {
      this._saveFormGroupValueChange = formGroup.valueChanges.subscribe(
        (value) => this.dataChanges.emit(value)
      );

      setTimeout(() => {
        this._isAutomaticValueChangeActive = false;
      });
    }),
    shareReplay(1) // IMPORTANT for performance e.g dependsOn
  );

  private _getInputValue(
    field: FormFields.Field,
    preFilledCustomForm: CustomFormData
  ) {
    if (field.type === 'checkbox') {
      // set value from BE first, if exists
      return field.is_readonly
        ? field.is_checked //DS-11366!!
        : preFilledCustomForm[field.postname] ??
            field.is_checked ??
            field.value;
    } else {
      const beCalculatedValue = !preFilledCustomForm[field.postname]
        ? field.value
        : preFilledCustomForm[field.postname];

      const generatorInputEl: GeneratorInputComponent =
        this.generatorInputs?.find((e) => e.field.postname === field.postname);

      /**
       * Wenn das Feld focusiert ist oder das BE einen '' als Value zurück gibt,
       * soll immer der Wert des Users genommen werden (falls vorhanden)
       */
      if (
        generatorInputEl &&
        (generatorInputEl.focus$.value || beCalculatedValue === '')
      ) {
        return generatorInputEl.getFormControl().value;
      }

      return beCalculatedValue;
    }
  }

  fromGroupValue$ = this.formGroup$.pipe(
    switchMap((formGroup) =>
      formGroup.valueChanges.pipe(startWith(formGroup.value))
    ),
    shareReplay(1)
  );

  // new structure only for template
  fieldStructure$ = this.fields$.pipe(
    map((fields) => mapFieldListToFormStructure(fields, this.fromGroupValue$))
  );

  fieldValidations$ = this._orderFormFascade.validation$.pipe(
    map((validations) => {
      return validations.errors_by_postname;
    }),
    shareReplay(1)
  );

  successFieldValidations$ = this._orderFormFascade.validation$.pipe(
    map((validations) => {
      return validations.success_by_postname;
    }),
    shareReplay(1)
  );

  dateFormat = null;
  hourFormat: 12 | 24 = null;

  trackByFieldSettings = (index, item: InterfaceField) => {
    // important to check all settings configurations
    return item.field.postname;
  };

  trackByStructure = (index, item: InterfaceFields) => {
    return item.fields.map((f) => f.field.postname).join(',');
  };

  private _subscriptionBeforeSubmit =
    this._orderFormFascade.formValidation.subscribe(() => {
      const component =
        this._element.nativeElement.closest('[ng-version]').tagName;
      if (
        this._excludeFormOrderValidation ||
        !isPageBuilderElementVisible(this._element.nativeElement)
      ) {
        return Promise.resolve({
          valid: true,
          component,
          invalidFieldList: [],
        });
      }
      return this._onBeforeSubmit();
    });

  constructor(
    private _orderFormFascade: OrderformFascade,
    private _cdr: ChangeDetectorRef,
    private _element: ElementRef<HTMLElement>
  ) {}

  ngOnInit(): void {
    /**
     * We need cdr there because we seperated the inputs in another component...
     * (Every input change would trigger the component cdr now we need do this manually)
     */
    this.fromGroupValue$
      .pipe(debounceTime(250), takeUntil(this._destroy$))
      .subscribe(() => {
        this._cdr.detectChanges();
      });

    this._syncData();
  }

  /**
   * we need also subscribe on data$ because the same widget can exist in mobile and desktop view
   * at the end, both widgets are rendered! We need to make sure, that both forms has same filled values
   * else the validation do not work! https://digistore.atlassian.net/browse/DS-10709
   */
  /**
   * @TODO: muss überarbeitet werden, es darf nur einen sync bei einer Manuellen-Eingabe geben!!!
   * @private
   */
  private _syncData() {
    this.data$
      .pipe(
        takeUntil(this._destroy$),
        debounceTime(100),
        distinctUntilChanged((a, b) => isEqual(a, b)),
        switchMap((updateData) => {
          // get the current form group
          return this.formGroup$.pipe(
            map((formGroup) => {
              return {
                formGroup,
                formGroupValue: updateData,
              };
            })
          );
        })
      )
      .subscribe((data) => {
        this.fields$.value.forEach((field) => {
          const formControl = data.formGroup.get(field.postname);
          const inputValue = this._getInputValue(field, data.formGroupValue);
          if (formControl.touched) {
            return;
          }
          formControl.patchValue(inputValue, {
            emitEvent: false,
          });
        });
        // If data transfers via input, trigger change detection
        this._cdr.detectChanges();
      });
  }

  /**
   * Gibt die Validators für ein Form-Field zurück.
   * @param field
   * @param backendValidationError
   * @private
   */
  private _getFieldValidatorList(
    control: AbstractControl,
    field: FormFields.Field,
    backendValidationError: {
      [key: string]: string;
    }
  ) {
    const validation: ValidatorFn[] = [];
    const required = field.is_required;

    if (required) {
      validation.push((tmpControl: AbstractControl) => {
        if (isFormFieldVisible(tmpControl.parent.value, field.depends_on)) {
          return Validators.required(tmpControl);
        }
        return null;
      });

      if (field.type === 'checkbox') {
        validation.push((validationControl) => {
          if (
            isFormFieldVisible(validationControl.parent.value, field.depends_on)
          ) {
            return typeof validationControl.value === 'boolean' &&
              validationControl.value
              ? null
              : { checkboxRequired: true };
          }
          return null;
        });
      }
    }

    if (field.is_latin_only) {
      validation.push((validationControl) => {
        if (
          isFormFieldVisible(validationControl.parent.value, field.depends_on)
        ) {
          /**
           * https://gist.github.com/germanattanasio/84cd25395688b7935182
           */
          const value = validationControl.value;
          const regExp =
            /^[A-z\u00C0-\u00ff\d\s'\.,-\/#!$%\^&\*;:{}=\-_`~()]+$/;
          const ableToReplaceAllChars = value.replace(regExp, '');

          /**
           * validate only if value exists
           * * value would exist, if field ist required
           */
          if (value) {
            /**
             * If able to replace all allowed Chars -> allow string
             */
            return ableToReplaceAllChars?.trim() === ''
              ? null
              : { latinOnly: true };
          }
        }
        return null;
      });
    }

    if (field.regex) {
      validation.push((validationControl) => {
        if (
          isFormFieldVisible(validationControl.parent.value, field.depends_on)
        ) {
          const value = validationControl.value;
          const regExp = field.regex;

          /**
           * validate only if value exists
           * * value would exist, if field ist required
           */
          if (value) {
            return value.match(regExp) ? null : { pattern: true };
          }
        }
        return null;
      });
    }

    if (field.validators) {
      validation.push(...field.validators);
    }

    // show backend errors for this postname field
    // validator returns always this error!
    if (backendValidationError[field.postname]) {
      validation.push((validationControl) => {
        return { custom: backendValidationError[field.postname] };
      });

      control.valueChanges
        .pipe(
          distinctUntilChanged(),
          filter(() => !this._isAutomaticValueChangeActive),
          take(1),
          debounceTime(10)
        )
        .subscribe((v) => {
          this._orderFormFascade.removeValidationError(field.postname);
        });
    }
    return validation;
  }

  private _fieldIsCheckboxList(field: FormFields.Field): boolean {
    return field.type === 'select' && field.multi_select && !!field.options;
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
    this._subscriptionBeforeSubmit.unsubscribe();
  }

  private async _onBeforeSubmit(): Promise<OrderFormValidationResult> {
    const component =
      this._element.nativeElement.closest('[ng-version]').tagName;
    formMarkAllAsTouched(this._formGroup);
    if (this._formGroup.pending) {
      await firstValueFrom(
        this._formGroup.statusChanges.pipe(
          filter((status) => status !== 'PENDING')
        )
      );
    }
    const invalidFieldList = getInvalidFieldListFromFormGroup(this._formGroup);
    return Promise.resolve({
      valid: this._formGroup.valid,
      component,
      invalidFieldList,
    });
  }

  public submit(): boolean {
    formMarkAllAsTouched(this._formGroup);
    return this._formGroup.valid;
  }

  public isValid(): boolean {
    return this._formGroup.valid;
  }

  public isFieldValid(field: string): boolean {
    return !!this._formGroup.get(field)?.valid;
  }
}
